import React, { useEffect, useRef, useState } from 'react';

import cx from 'classnames';
import { Check, ChevronDown } from 'react-feather';

import styles from './ThemedSelect.module.scss';

/**
 * Custom dropdown styled to the project theme — a drop-in replacement for a
 * native <select>, whose open option list is drawn by the OS and can't be
 * themed (it renders as the default grey list).
 *
 * Props:
 *   id               — id for the trigger button (pair with a <label htmlFor>)
 *   value            — currently selected option value ('' for none)
 *   options          — array of { value, label }
 *   placeholder      — shown on the trigger while nothing is selected
 *   onChange         — called with the picked option's value
 *   triggerClassName — extra class for the trigger, to adjust sizing per use
 */
const ThemedSelect = ({ id, value, options = [], placeholder = 'Select an option', onChange, triggerClassName }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const wrapRef = useRef(null);

  const selectedOption = options.find(o => String(o.value) === String(value));

  // Close when clicking anywhere outside the dropdown.
  useEffect(() => {
    if (!isOpen) return undefined;
    const handleClickOutside = e => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      setHighlightedIndex(Math.max(0, options.findIndex(o => String(o.value) === String(value))));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const selectOption = option => {
    onChange(option.value);
    setIsOpen(false);
  };

  const handleKeyDown = e => {
    if (!isOpen) {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) {
        e.preventDefault();
        setIsOpen(true);
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex(i => Math.min(options.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(i => Math.max(0, i - 1));
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (options[highlightedIndex]) selectOption(options[highlightedIndex]);
    } else if (e.key === 'Escape' || e.key === 'Tab') {
      setIsOpen(false);
    }
  };

  return (
    <div className={styles.dropdown} ref={wrapRef}>
      <button
        type="button"
        id={id}
        className={cx(styles.trigger, isOpen && styles.triggerOpen, triggerClassName)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
      >
        <span className={cx(styles.triggerLabel, !selectedOption && styles.placeholder)}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <ChevronDown size={16} className={cx(styles.chevron, isOpen && styles.chevronOpen)} />
      </button>

      {isOpen && (
        <ul className={styles.menu} role="listbox" aria-labelledby={id}>
          {options.map((option, index) => {
            const isActive = String(option.value) === String(value);
            return (
              <li
                key={option.value}
                role="option"
                aria-selected={isActive}
                className={cx(
                  styles.option,
                  isActive && styles.optionSelected,
                  index === highlightedIndex && styles.optionHighlighted,
                )}
                onMouseEnter={() => setHighlightedIndex(index)}
                onClick={() => selectOption(option)}
              >
                {option.label}
                {isActive && <Check size={14} />}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default ThemedSelect;
